import './App.scss';
import { ReactComponent as MaskGroupApproved } from './assets/maskGroupApproved.svg';
import { ReactComponent as MaskGroupReject } from './assets/maskGroupReject.svg';
import { ReactComponent as Approved } from './assets/approved.svg';
import { ReactComponent as Reject } from './assets/reject.svg';
import { ReactComponent as Logo } from './assets/logo.svg';
import { useEffect, useState } from 'react';
import { get, persianDigit, priceDigit, getBankNameFromIban } from './script';
import { DigitalReceiptResponse } from './interface';
import { APIRoutes } from './data';
import { BankIcon, Card, Loading } from './components';
import ENV from './env.json';
import moment from 'jalali-moment';

function App() {
    enum StateEnum {
        Approved,
        Rejected,
    }

    const [state, setState] = useState<StateEnum>(StateEnum.Rejected);
    const [data, setData] = useState<DigitalReceiptResponse | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState<boolean>(false);

    useEffect(() => {
        const trackingCode = window.location.pathname.split('/')[1];
        get<DigitalReceiptResponse>(APIRoutes.digitalReceipts(trackingCode))
            .then((receiptResponse) => {
                const data = receiptResponse.value.value as DigitalReceiptResponse;
                const iban = data.iBan;
                const formattedIban = iban.startsWith('IR') ? iban : `IR${iban}`;
                setData({ ...data, iBan: formattedIban });
                setState(data.state.id !== 3 ? StateEnum.Approved : StateEnum.Rejected);
            })
            .catch((e) => {
                setIsError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    if (isLoading) return <Loading />;
    if (isError)
        return (
            <div className="w-screen h-screen flex items-center justify-center">
                <h1 className="text-2xl text-[#1C1D1F] font-medium opacity-50">خطایی رخ داده است</h1>
            </div>
        );

    return (
        <div className="max-md:px-4 w-full min-h-screen bg-background flex items-center justify-center pb-32 pt-24 max-md:pt-12 max-md:pb-12 overflow-x-hidden">
            <div className="max-w-[600px] max-md:max-w-full w-full bg-white relative rounded-2xl">
                <div className="absolute -top-10 w-full h-20 flex justify-center items-center">
                    <div
                        className={`relative w-20 h-20 max-md:h-16 max-md:w-16 rounded-full flex items-center justify-center border backdrop-blur-md ${
                            state === StateEnum.Approved
                                ? 'bg-[#53BDA31A] border-[#2EA37C33]'
                                : 'bg-[#BD53651A] border-[#A32E4233]'
                        }`}
                    >
                        <div
                            className={`w-10 h-10 max-md:h-8 max-md:w-8 rounded-full ${
                                state === StateEnum.Approved ? 'bg-[#53BDA3]' : 'bg-[#BD5365]'
                            } flex items-center justify-center`}
                        >
                            {state === StateEnum.Approved ? <Approved /> : <Reject />}
                        </div>
                        {state === StateEnum.Approved ? (
                            <MaskGroupApproved className="absolute top-1/2" />
                        ) : (
                            <MaskGroupReject className="absolute top-1/2 z-0" />
                        )}
                    </div>
                </div>
                <div className="z-10 relative mt-20 pb-10 border-b-4 border-dashed border-background">
                    <h2 className="text-blackTitle text-base max-md:text-sm font-bold w-full text-center">
                        {state === StateEnum.Rejected ? 'تراکنش نا موفق بود!' : 'تراکنش با موفقیت انجام شد!'}
                    </h2>
                </div>
                <div className="w-full h-12 absolute top-[120px] left-0">
                    <div className="relative w-full h-full">
                        <div className="absolute top-0 -left-6 w-12 h-12 rounded-full bg-background" />
                        <div className="absolute top-0 -right-6 w-12 h-12 rounded-full bg-background" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-6 max-md:gap-4 p-10 relative z-10">
                    {data?.amount && (
                        <Card
                            title="مبلغ"
                            content={
                                <p className="text-base font-bold text-blackText max-md:text-sm">
                                    {priceDigit(data.amount)} تومان
                                </p>
                            }
                        />
                    )}
                    {(data?.firstName || data?.lastName) && (
                        <Card
                            title="دریافت کننده"
                            content={
                                <p className="text-base font-bold text-blackText max-md:text-sm">
                                    {data.firstName ?? ''} {data.lastName ?? ''}
                                </p>
                            }
                        />
                    )}
                    {data?.iBan && (
                        <Card
                            title="شماره شبا"
                            isCopyable={{ data: data.iBan, label: 'شماره شبا' }}
                            content={
                                <div className="flex items-center gap-1 justify-end  overflow-hidden w-full">
                                    <p className="text-base font-bold text-blackText max-md:text-sm en whitespace-nowrap text-ellipsis overflow-hidden">
                                        {persianDigit(data.iBan)}
                                    </p>
                                    <BankIcon className="w-4 h-4 mb-1" symbol={getBankNameFromIban(data.iBan)} />
                                </div>
                            }
                        />
                    )}
                    {data?.state && (
                        <Card
                            title="وضعیت"
                            content={
                                <div className="flex items-center gap-2">
                                    <div
                                        className={`w-2 h-2 rounded-full ${
                                            state === StateEnum.Approved ? 'bg-[#53BDA3]' : 'bg-[#8F3343]'
                                        }`}
                                    />
                                    <p
                                        className={`text-base font-bold max-md:text-sm en ${
                                            state === StateEnum.Approved ? 'text-[#53BDA3]' : 'text-[#8F3343]'
                                        }`}
                                    >
                                        {data.state.title}
                                    </p>
                                </div>
                            }
                        />
                    )}
                    {data?.createdOn && (
                        <Card
                            title="تاریخ و زمان واریز"
                            content={
                                <div className="flex items-center gap-1">
                                    <p className="font-bold text-base text-blackText max-md:text-sm">
                                        {
                                            moment.utc(data.createdOn).locale('fa').local().format(' HH:mm')

                                        }
                                    </p>
                                    -
                                    <p className="font-bold text-base text-blackText max-md:text-sm">

                                            {moment.utc(data.createdOn).locale('fa').local().format('jYYYY/jM/jD - HH:mm')}


                                    </p>
                                </div>
                            }
                        />
                    )}
                    {data?.estimatedDepositTime && (
                        <Card
                            title="زمان تخمینی واریز"
                            content={
                                <div className="flex items-center gap-1">
                                    <p className="font-bold text-base text-blackText max-md:text-sm">
                                        {new Date(data.estimatedDepositTime).toLocaleTimeString('fa', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </p>
                                    -
                                    <p className="font-bold text-base text-blackText max-md:text-sm">
                                        {new Date(data.estimatedDepositTime).toLocaleDateString('fa')}
                                    </p>
                                </div>
                            }
                        />
                    )}
                    {data?.type && (
                        <Card title="نوع" content={<p className="text-base font-bold">{data.type.title}</p>} />
                    )}
                    {data?.trackingCode && (
                        <Card
                            title="شناسه"
                            isCopyable={{ data: data.trackingCode, label: 'شناسه' }}
                            content={
                                <p className="text-base font-bold text-blackText max-md:text-sm en whitespace-nowrap overflow-hidden text-ellipsis">
                                    {data.trackingCode}
                                </p>
                            }
                        />
                    )}
                </div>
                <div className="w-full flex justify-center items-center z-10 relative pb-4">
                    <div className="w-full h-[1px] bg-[#F5F8FF] flex-1" />
                    <a className="cursor-pointer transition-opacity hover:opacity-70" href={ENV.pmx}>
                        <Logo className="mx-4 -mt-2" />
                    </a>
                    <div className="w-full h-[1px] bg-[#F5F8FF] flex-1" />
                </div>
                <div className="absolute -bottom-[56px] max-md:-bottom-[24px] left-0 w-full">
                    <svg
                        className="w-full h-full"
                        width="601"
                        height="74"
                        viewBox="0 0 601 74"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M17.4427 71.2954L2.32732 56.0951C0.836713 54.5961 0 52.5681 0 50.4541V8C0 3.58172 3.58172 0 8 0H593C597.418 0 601 3.58172 601 7.99999V50.4541C601 52.5681 600.163 54.5961 598.673 56.0951L583.557 71.2954C580.43 74.4408 575.34 74.4408 572.212 71.2954L560.442 59.4593C557.314 56.3139 552.224 56.3139 549.097 59.4593L537.327 71.2954C534.199 74.4408 529.109 74.4408 525.981 71.2954L514.211 59.4593C511.083 56.3139 505.994 56.3139 502.866 59.4593L491.096 71.2954C487.968 74.4408 482.878 74.4408 479.75 71.2954L467.98 59.4593C464.853 56.3139 459.763 56.3139 456.635 59.4593L444.865 71.2954C441.737 74.4408 436.647 74.4408 433.52 71.2954L421.75 59.4593C418.622 56.3139 413.532 56.3139 410.404 59.4593L398.634 71.2954C395.506 74.4408 390.417 74.4408 387.289 71.2954L375.519 59.4593C372.391 56.3139 367.301 56.3139 364.173 59.4593L352.403 71.2954C349.276 74.4408 344.186 74.4408 341.058 71.2954L329.288 59.4593C326.16 56.3139 321.07 56.3139 317.943 59.4593L306.173 71.2954C303.045 74.4408 297.955 74.4408 294.827 71.2954L283.057 59.4593C279.93 56.3139 274.84 56.3139 271.712 59.4593L259.942 71.2954C256.814 74.4408 251.724 74.4408 248.597 71.2954L236.827 59.4593C233.699 56.3139 228.609 56.3139 225.481 59.4593L213.711 71.2954C210.583 74.4408 205.494 74.4408 202.366 71.2954L190.596 59.4593C187.468 56.3139 182.378 56.3139 179.25 59.4593L167.48 71.2954C164.353 74.4408 159.263 74.4408 156.135 71.2954L144.365 59.4593C141.237 56.3139 136.147 56.3139 133.02 59.4593L121.25 71.2954C118.122 74.4408 113.032 74.4408 109.904 71.2954L98.1342 59.4593C95.0064 56.3139 89.9166 56.3139 86.7889 59.4593L75.0188 71.2954C71.8911 74.4408 66.8012 74.4408 63.6735 71.2954L51.9035 59.4593C48.7757 56.3139 43.6859 56.3139 40.5581 59.4593L28.7881 71.2954C25.6603 74.4408 20.5705 74.4408 17.4427 71.2954Z"
                            fill="white"
                        />
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default App;
